import { useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Lock,
  MailCheck,
  MoreHorizontal,
  Package,
  ShieldAlert,
  Trash2,
  Truck,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { useComplaintDetail } from "@/hooks/useComplaints";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import type { ComplaintDetailResponse } from "@/api/types";
import { buildFooterPlan, buildNextStep, type FooterSlot } from "./footerPlan";
import { ComplaintStageBadge, SeverityBadge, SlaBadge } from "./badges";
import { AttachmentsPanel } from "./AttachmentsPanel";
import { DraftEditForm } from "./DraftEditForm";
import { EventTimeline } from "./EventTimeline";
import { CreateInternalRequestDrawer } from "@/components/requests/CreateInternalRequestDrawer";
import { ResolveDrawer } from "./ResolveDrawer";
import { SupplierSuggestionCard } from "./SupplierSuggestionCard";
import { SupplierSendModal } from "./SupplierSendModal";
import { SupplierResponseModal } from "./SupplierResponseModal";
import {
  ApproveFinancialModal,
  AssignModal,
  CloseModal,
  ConfirmModal,
  DeleteDraftModal,
  EscalateModal,
  ReasonModal,
  SeverityModal,
  SlaOverrideModal,
} from "./ActionModals";
import {
  CHANNEL_LABEL,
  PRODUCT_TYPE_LABEL,
  QUEUE_LABEL,
  RESOLUTION_TYPE_LABEL,
  TYPE_LABEL,
  formatDateTime,
  formatMoney,
} from "./constants";

type ModalKind =
  | "submit"
  | "respond"
  | "awaiting"
  | "client-responded"
  | "resolve"
  | "cancel-proposal"
  | "approve"
  | "request-changes"
  | "apply"
  | "revoke"
  | "reject"
  | "escalate"
  | "de-escalate"
  | "close"
  | "reopen"
  | "severity"
  | "sla"
  | "assign"
  | "claim"
  | "return-queue"
  | "reroute"
  | "request-dept"
  | "leave-unassigned"
  | "prepare-supplier"
  | "add-supplier-response"
  | "delete"
  | null;

export function ComplaintDetailDrawer({
  complaintId,
  open,
  onClose,
}: {
  complaintId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const detailQ = useComplaintDetail(open ? complaintId : null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (!open) return null;
  const c = detailQ.data;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Деталі рекламації"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col bg-background shadow-2xl outline-none sm:w-[640px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold">{c?.number ?? "Рекламація"}</h3>
              {c && <ComplaintStageBadge stage={c.businessStage} state={c.currentState} />}
              {c && <SeverityBadge severity={c.severity} />}
              {c && <SlaBadge sla={c.sla} />}
            </div>
            {c?.account && <p className="mt-0.5 truncate text-sm text-muted-foreground">{c.account.name}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {detailQ.isPending ? (
            <div className="flex h-40 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : detailQ.isError || !c ? (
            <div className="rounded-lg border bg-destructive/5 p-6 text-center">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
              <p className="text-sm text-destructive">
                {(detailQ.error as Error)?.message ?? "Не вдалося завантажити"}
              </p>
            </div>
          ) : (
            <DetailBody
              c={c}
              onConflict={() => {
                setToast("Чернетку оновлено в іншому місці — показано свіжу версію.");
                void detailQ.refetch();
              }}
              onRequestDelete={() => setModal("delete")}
            />
          )}
        </div>

        {toast && (
          <div className="pointer-events-none absolute inset-x-0 bottom-20 z-20 flex justify-center px-4">
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-success-foreground/20 bg-success px-3 py-2 text-sm text-success-foreground shadow-lg">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              <span>{toast}</span>
              <button onClick={() => setToast(null)} className="shrink-0 opacity-60 hover:opacity-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {c && <ActionFooter c={c} onAction={setModal} />}
      </div>

      {/* action modals / drawers — render above the detail drawer */}
      {complaintId && c && (
        <>
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "submit"}
            onClose={() => setModal(null)}
            action="submit"
            title="Подати рекламацію"
            body="Рекламацію буде подано та авто-призначено. Для товарних рекламацій перевіряються обов’язкові поля."
            confirmLabel="Подати"
          />
          <DeleteDraftModal
            complaintId={complaintId}
            open={modal === "delete"}
            onClose={() => setModal(null)}
            onDone={onClose}
          />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "respond"}
            onClose={() => setModal(null)}
            action="respond"
            title="Первинна відповідь"
            body="Зафіксувати, що первинну відповідь клієнту надано?"
            confirmLabel="Підтвердити"
          />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "awaiting"}
            onClose={() => setModal(null)}
            action="mark-awaiting-client"
            title="Очікувати клієнта"
            body="Перевести в стан очікування відповіді клієнта?"
            confirmLabel="Перевести"
          />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "client-responded"}
            onClose={() => setModal(null)}
            action="client-responded"
            title="Клієнт відповів"
            body="Повернути рекламацію в роботу?"
            confirmLabel="В роботу"
          />
          <ResolveDrawer complaintId={complaintId} open={modal === "resolve"} onClose={() => setModal(null)} />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "cancel-proposal"}
            onClose={() => setModal(null)}
            action="cancel-proposal"
            title="Скасувати пропозицію"
            body="Скасувати запропоноване рішення та повернути в роботу?"
            confirmLabel="Скасувати пропозицію"
            destructive
          />
          <ApproveFinancialModal
            complaintId={complaintId}
            open={modal === "approve"}
            onClose={() => setModal(null)}
            proposal={c.proposal}
          />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "request-changes"}
            onClose={() => setModal(null)}
            action="reject-financial"
            title="Повернути на доопрацювання"
            label="Причина повернення"
            minChars={50}
            confirmLabel="Повернути"
            hint="Менеджер доопрацює пропозицію"
          />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "apply"}
            onClose={() => setModal(null)}
            action="apply-resolution"
            title="Застосувати рішення"
            body="Застосувати погоджене фінансове рішення? Цілісність доказів буде повторно перевірено."
            confirmLabel="Застосувати"
          />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "revoke"}
            onClose={() => setModal(null)}
            action="revoke-approval"
            title="Відкликати погодження"
            label="Причина відкликання"
            minChars={100}
            confirmLabel="Відкликати"
            destructive
          />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "reject"}
            onClose={() => setModal(null)}
            action="reject"
            title="Відхилити рекламацію"
            label="Причина відхилення"
            minChars={10}
            confirmLabel="Відхилити"
            destructive
          />
          <EscalateModal complaintId={complaintId} open={modal === "escalate"} onClose={() => setModal(null)} />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "de-escalate"}
            onClose={() => setModal(null)}
            action="de-escalate"
            title="Повернути від керівника"
            body="Повернути рекламацію від керівника до попереднього стану для продовження роботи?"
            confirmLabel="Повернути від керівника"
          />
          <CloseModal complaintId={complaintId} open={modal === "close"} onClose={() => setModal(null)} />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "reopen"}
            onClose={() => setModal(null)}
            action="reopen"
            title="Відкрити повторно"
            label="Причина повторного відкриття"
            minChars={50}
            confirmLabel="Відкрити"
          />
          <SeverityModal
            complaintId={complaintId}
            open={modal === "severity"}
            onClose={() => setModal(null)}
            current={c.severity}
          />
          <SlaOverrideModal complaintId={complaintId} open={modal === "sla"} onClose={() => setModal(null)} />
          <AssignModal complaintId={complaintId} open={modal === "assign"} onClose={() => setModal(null)} />
          <ConfirmModal
            complaintId={complaintId}
            open={modal === "claim"}
            onClose={() => setModal(null)}
            action="claim"
            title="Взяти з черги"
            body="Взяти цю рекламацію з черги служби якості / підтримки в роботу на себе?"
            confirmLabel="Взяти"
          />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "return-queue"}
            onClose={() => setModal(null)}
            action="return-to-queue"
            title="Повернути в чергу"
            label="Причина (необов’язково)"
            minChars={0}
            confirmLabel="Повернути"
          />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "reroute"}
            onClose={() => setModal(null)}
            onDone={() => {
              setToast(
                c.assignedQueue === "supportQuality"
                  ? "Рекламацію передано менеджеру клієнта"
                  : "Рекламацію передано в службу якості / підтримки",
              );
              void detailQ.refetch();
            }}
            action="reroute"
            title={
              c.assignedQueue === "supportQuality"
                ? "Передати менеджеру клієнта"
                : "Передати рекламацію в службу якості / підтримки"
            }
            description={
              c.assignedQueue === "supportQuality"
                ? "Після передачі рекламація повернеться до менеджера клієнта — він продовжить обробку."
                : "Після передачі рекламація з’явиться в черзі служби якості / підтримки. Менеджер клієнта залишиться прив’язаним, але обробку вестиме відповідальна черга — перевірить товар, партію, фото/відео та запропонує рішення."
            }
            label="Причина передачі"
            placeholder="Напр.: потрібна експертиза якості товару, перевірка партії…"
            minChars={10}
            maxChars={500}
            validationText="Вкажіть причину передачі, мінімум 10 символів."
            confirmLabel={c.assignedQueue === "supportQuality" ? "Передати менеджеру" : "Передати в службу якості"}
            extraBody={{ targetQueue: c.assignedQueue === "supportQuality" ? "manager" : "supportQuality" }}
          />
          <ReasonModal
            complaintId={complaintId}
            open={modal === "leave-unassigned"}
            onClose={() => setModal(null)}
            action="leave-unassigned"
            title="Залишити без призначення"
            description="Рекламація залишиться без відповідального. Вона буде видима в черзі «Без призначення». Рішення фіксується в історії."
            label="Причина"
            minChars={10}
            maxChars={500}
            validationText="Вкажіть причину, мінімум 10 символів."
            confirmLabel="Залишити в черзі"
          />
          <CreateInternalRequestDrawer
            open={modal === "request-dept"}
            onClose={() => {
              setModal(null);
              void detailQ.refetch(); // post-write refresh — show the new cross-dept request
            }}
            complaintId={complaintId}
            sourceModule="complaint"
            sourceEntityId={complaintId}
          />
          {/* D-133 C-W3 — 2-step prepare→send supplier email */}
          <SupplierSendModal
            complaintId={complaintId}
            open={modal === "prepare-supplier"}
            onClose={() => setModal(null)}
            onSent={(res) => {
              setModal(null);
              const skipped = res.skipped?.length
                ? ` Не прикріплено (завеликі): ${res.skipped.map((s) => s.name).join(", ")}.`
                : "";
              setToast(
                res.duplicate
                  ? "Це звернення вже було надіслано постачальнику."
                  : `Звернення постачальнику надіслано (прийнято поштовим relay).${skipped}`,
              );
              void detailQ.refetch();
            }}
          />
          {/* D-133 E-W2 — manual «Додати відповідь постачальника» */}
          <SupplierResponseModal
            complaintId={complaintId}
            supplierName={c.supplier?.lastSupplierName}
            open={modal === "add-supplier-response"}
            onClose={() => setModal(null)}
            onAdded={() => {
              setModal(null);
              setToast("Відповідь постачальника збережено.");
              void detailQ.refetch();
            }}
          />
        </>
      )}
    </div>
  );
}

// ── body ──────────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

function queueBadgeClass(q: string): string {
  if (q === "manual_assignment") return "bg-destructive/10 text-destructive";
  if (q === "supportQuality") return "bg-warning text-warning-foreground";
  return "bg-primary/10 text-primary"; // manager
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-0.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value ?? "—"}</span>
    </div>
  );
}

function DetailBody({
  c,
  onConflict,
  onRequestDelete,
}: {
  c: ComplaintDetailResponse;
  onConflict: () => void;
  onRequestDelete: () => void;
}) {
  const isDraft = c.currentState === "draft";
  return (
    <div className="space-y-6">
      {/* W1 — per-state next-step instruction (replaces generic banners). */}
      <NextStepBanner c={c} />

      {/* COMP-S3 W5/W7 — a draft is editable inline + shows exactly what blocks submit. */}
      {isDraft && <SubmitReadinessBanner c={c} />}
      {isDraft && <DraftEditForm c={c} onConflict={onConflict} />}

      {/* COMP-S3-FIX W4 — author may delete his own draft (backend canDelete gate). */}
      {isDraft && c.allowedActions.canDelete && (
        <div className="flex justify-end border-t pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRequestDelete}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Видалити чернетку
          </Button>
        </div>
      )}

      {!isDraft && (
        <>
      {/* W4 — «Контрольні терміни» (was "SLA"). */}
      <Section title="Контрольні терміни">
        <div className="grid grid-cols-2 gap-3">
          <SlaCard
            label="Перша відповідь клієнту"
            dueAt={c.sla.initialResponse.dueAt}
            breached={c.sla.initialResponse.breached}
            doneAt={c.sla.initialResponse.sentAt}
          />
          <SlaCard label="Вирішення питання" dueAt={c.sla.resolution.dueAt} breached={c.sla.resolution.breached} />
        </div>
        {c.slaOverride && (
          <p className="text-[12px] text-muted-foreground">
            Строки перевизначено {formatDateTime(c.slaOverride.at)}
            {c.slaOverride.by?.name ? ` · ${c.slaOverride.by.name}` : ""}
          </p>
        )}
      </Section>

      <Section title="Опис">
        <p className="text-sm font-medium">{c.summary}</p>
        <p className="whitespace-pre-wrap text-sm text-foreground/80">{c.description}</p>
      </Section>

      <Section title="Деталі">
        <div className="rounded-md border bg-muted/20 px-3 py-2">
          <Field label="Тип" value={c.complaintType ? TYPE_LABEL[c.complaintType] : "—"} />
          <Field label="Канал" value={c.channel ? CHANNEL_LABEL[c.channel] : "—"} />
          <Field label="Отримано" value={formatDateTime(c.receivedAt)} />
          {c.assignedQueue && (
            <Field
              label="Черга"
              value={
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${queueBadgeClass(c.assignedQueue)}`}>
                  {QUEUE_LABEL[c.assignedQueue] ?? c.assignedQueue}
                </span>
              }
            />
          )}
          <Field label="Відповідальний" value={c.assignedUser?.name ?? (c.assignedQueue === "supportQuality" ? "черга (не взято)" : "—")} />
          <Field label="Менеджер клієнта" value={c.clientResponsibleManager?.name} />
          {c.createdBy && <Field label="Автор" value={c.createdBy.name} />}
          {c.clientResponsibleManagerSnapshotAtCreate && (
            <Field label="Менеджер на момент реєстрації" value={c.clientResponsibleManagerSnapshotAtCreate.name} />
          )}
          {c.contact && <Field label="Контакт" value={c.contact.name} />}
          {c.contract && <Field label="Договір" value={c.contract.name} />}
        </div>
      </Section>

      {c.product.linkedToProduct && (
        <Section title="Товар">
          <div className="rounded-md border bg-muted/20 px-3 py-2">
            <div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
              <Package className="h-4 w-4 text-muted-foreground" aria-hidden />
              {c.product.name || c.product.article || "Товар"}
            </div>
            <Field label="Тип" value={c.product.productType ? PRODUCT_TYPE_LABEL[c.product.productType] : "—"} />
            <Field label="Артикул" value={c.product.article} />
            <Field
              label="Бренд"
              value={
                <span className="inline-flex items-center gap-1.5">
                  {c.product.brand.display}
                  {c.product.brand.unverified ? (
                    <span className="rounded bg-warning px-1.5 py-0.5 text-[10px] font-semibold text-warning-foreground">
                      не підтверджено
                    </span>
                  ) : c.product.brand.dqFlag ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      потребує даних
                    </span>
                  ) : null}
                </span>
              }
            />
            <Field
              label="Партія"
              value={c.product.batchUnknown ? <span className="text-warning-foreground">невідома</span> : c.product.batch}
            />
            <Field label="Термін придатності" value={c.product.expiryDate} />
            <Field label="Кількість" value={c.product.affectedQuantity} />
          </div>
        </Section>
      )}

      {/* D-133 Фаза A — read-only постачальник (авто-пропозиція по бренду) */}
      {c.product.brand?.biBrandId && (
        <Section title="Постачальник">
          <SupplierSuggestionCard complaintId={c.id} />
        </Section>
      )}
        </>
      )}

      {/* D-133 E-W1 — supplier-status card (presentation-cache; 0 machine state) */}
      {c.supplier && c.supplier.status !== "none" && (
        <Section title="Звернення постачальнику">
          <div className="rounded-md border bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              {c.supplier.status === "replied" ? (
                <MailCheck className="h-4 w-4 text-success-foreground" aria-hidden />
              ) : (
                <Truck className="h-4 w-4 text-warning-foreground" aria-hidden />
              )}
              {c.supplier.status === "replied"
                ? "Отримано відповідь постачальника"
                : "Очікуємо постачальника"}
            </div>
            <div className="mt-1.5">
              {c.supplier.lastSupplierName && (
                <Field label="Постачальник" value={c.supplier.lastSupplierName} />
              )}
              <Field
                label="Надіслано"
                value={c.supplier.sentAt ? formatDateTime(c.supplier.sentAt) : "—"}
              />
              {c.supplier.status === "replied" && (
                <Field
                  label="Відповідь отримано"
                  value={c.supplier.repliedAt ? formatDateTime(c.supplier.repliedAt) : "—"}
                />
              )}
            </div>
          </div>
        </Section>
      )}

      {c.proposal && (
        <Section title="Запропоноване рішення">
          <div className="rounded-md border bg-muted/20 px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{RESOLUTION_TYPE_LABEL[c.proposal.type]}</span>
              {c.proposal.locked && (
                <span className="inline-flex items-center gap-1 text-[12px] text-success-foreground">
                  <Lock className="h-3 w-3" aria-hidden /> заблоковано
                </span>
              )}
            </div>
            {c.proposal.amount != null && (
              <Field label="Сума" value={formatMoney(c.proposal.amount, c.proposal.currency)} />
            )}
            <Field label="Статус погодження" value={c.approval.status} />
            <Field label="Доказів" value={c.proposal.evidence.length} />
            {c.proposal.snapshotSha256 && (
              <Field label="SHA-256" value={<span className="font-mono text-[11px]">{c.proposal.snapshotSha256.slice(0, 16)}…</span>} />
            )}
            {c.approval.rejectionReason && (
              <p className="mt-1 text-[13px] text-destructive">{c.approval.rejectionReason}</p>
            )}
          </div>
        </Section>
      )}

      {c.resolution && (
        <Section title="Застосоване рішення">
          <div className="rounded-md border border-success-foreground/20 bg-success px-3 py-2">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <CheckCircle2 className="h-4 w-4 text-success-foreground" aria-hidden />
              {RESOLUTION_TYPE_LABEL[c.resolution.type]}
            </div>
            {c.resolution.amount != null && (
              <Field label="Сума" value={formatMoney(c.resolution.amount, c.resolution.currency)} />
            )}
            {c.resolution.description && (
              <p className="mt-1 text-[13px] text-foreground/80">{c.resolution.description}</p>
            )}
            <Field label="Застосовано" value={formatDateTime(c.resolution.appliedAt)} />
          </div>
        </Section>
      )}

      {c.escalation && (
        <Section title="Ескалація">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <ShieldAlert className="h-4 w-4 text-destructive" aria-hidden />
              Рівень {c.escalation.level}
            </div>
            {c.escalation.reason && <p className="mt-1 text-[13px] text-foreground/80">{c.escalation.reason}</p>}
            <Field label="Ескальовано" value={formatDateTime(c.escalation.escalatedAt)} />
          </div>
        </Section>
      )}

      <Section title="Вкладення">
        <AttachmentsPanel
          complaintId={c.id}
          attachments={c.attachments}
          canUpload={c.allowedActions.canUploadAttachment}
          canDownload={c.allowedActions.canDownloadEvidence}
          evidenceLocked={c.allowedActions.evidenceLocked}
          state={c.currentState}
        />
      </Section>

      {c.crossDeptRequests.length > 0 && (
        <Section title="Запити у відділи">
          <div className="space-y-1.5">
            {c.crossDeptRequests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.subject || r.requestType}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.department?.label ?? "—"} · {r.status}
                  </div>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{formatDateTime(r.createdAt)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Історія">
        <EventTimeline events={c.history} />
      </Section>
    </div>
  );
}

function SlaCard({
  label,
  dueAt,
  breached,
  doneAt,
}: {
  label: string;
  dueAt: string | null;
  breached: boolean;
  doneAt?: string | null;
}) {
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2",
        breached
          ? "border-destructive/40 bg-destructive/5"
          : doneAt
            ? "border-success-foreground/20 bg-success"
            : "border-border bg-muted/20",
      )}
    >
      <div className="text-[12px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 flex items-center gap-1 text-sm font-medium">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        {formatDateTime(dueAt)}
      </div>
      {breached ? (
        <div className="text-[12px] font-medium text-destructive">Порушено</div>
      ) : doneAt ? (
        <div className="text-[12px] text-success-foreground">Виконано {formatDateTime(doneAt)}</div>
      ) : null}
    </div>
  );
}

// ── next-step banner (W1) ─────────────────────────────────────────────────────

function NextStepBanner({ c }: { c: ComplaintDetailResponse }) {
  const b = buildNextStep(c);
  const Icon = b.icon;
  const tone =
    b.tone === "act"
      ? "border-warning-foreground/20 bg-warning text-warning-foreground"
      : b.tone === "done"
        ? "border-success-foreground/20 bg-success text-success-foreground"
        : "border-border bg-muted/40 text-foreground";
  return (
    <div className={cn("flex items-start gap-2.5 rounded-md border px-3 py-2.5", tone)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-sm font-semibold">{b.title}</div>
        <div className="text-[13px] opacity-90">{b.desc}</div>
      </div>
    </div>
  );
}

// ── footer: 1 primary CTA + secondary + «Інші дії» overflow (W2/W3/W6/W8) ─────
// All slots come from buildFooterPlan, which gates every action on the backend
// allowedActions booleans (production-rule #2). closed/rejected → read-only (W3).

// COMP-S3 W7 — soft submit gate: list what's missing (same backend single source as
// the hard gate) so «Подати» is explained-disabled, never a fill-and-reject 400.
function SubmitReadinessBanner({ c }: { c: ComplaintDetailResponse }) {
  const r = c.submitReadiness;
  if (r.canSubmit) {
    return (
      <div className="flex items-start gap-2.5 rounded-md border border-success-foreground/20 bg-success px-3 py-2.5 text-success-foreground">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="text-[13px] font-medium">Готово до подання — усі обов’язкові поля заповнено.</div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2.5 text-warning-foreground">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden /> Щоб подати рекламацію, додайте:
      </div>
      <ul className="mt-1 list-disc pl-7 text-[13px]">
        {r.missingFields.map((m) => (
          <li key={m.key}>{m.label}</li>
        ))}
      </ul>
    </div>
  );
}

function CtaButton({
  slot,
  variant,
  onAction,
  disabled,
}: {
  slot: FooterSlot;
  variant: "default" | "outline";
  onAction: (m: ModalKind) => void;
  disabled?: boolean;
}) {
  const Icon = slot.icon;
  return (
    <Button variant={variant} disabled={disabled} onClick={() => onAction(slot.key)} className="gap-1.5">
      <Icon className="h-4 w-4" aria-hidden /> {slot.label}
    </Button>
  );
}

function OverflowItem({ slot, onClick }: { slot: FooterSlot; onClick: () => void }) {
  const Icon = slot.icon;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-2.5 px-3 py-2 text-left text-sm hover:bg-muted",
        slot.danger && "text-destructive",
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{slot.label}</span>
        {slot.consequence && (
          <span className="block text-[11px] text-muted-foreground">{slot.consequence}</span>
        )}
      </span>
    </button>
  );
}

function ActionFooter({
  c,
  onAction,
}: {
  c: ComplaintDetailResponse;
  onAction: (m: ModalKind) => void;
}) {
  const plan = buildFooterPlan(c);
  const [menuOpen, setMenuOpen] = useState(false);

  const hasAny = plan.primary || plan.secondary || plan.overflow.length > 0 || plan.readOnly;
  if (!hasAny) return null;

  return (
    <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t bg-background/95 px-5 py-3 backdrop-blur">
      {plan.readOnly && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" aria-hidden /> {plan.readOnly}
        </div>
      )}
      {plan.primary && (
        <CtaButton
          slot={plan.primary}
          variant="default"
          onAction={onAction}
          disabled={plan.primary.key === "submit" && !c.submitReadiness.canSubmit}
        />
      )}
      {plan.secondary && <CtaButton slot={plan.secondary} variant="outline" onAction={onAction} />}
      {plan.overflow.length > 0 && (
        <div className="relative ml-auto">
          <Button
            variant="outline"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="gap-1.5"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden /> Інші дії
          </Button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" aria-hidden onClick={() => setMenuOpen(false)} />
              <div
                role="menu"
                className="absolute bottom-full right-0 z-20 mb-2 max-h-[60vh] w-72 overflow-y-auto rounded-md border bg-background py-1 shadow-xl"
              >
                {plan.overflow.map((s) => (
                  <OverflowItem
                    key={s.key}
                    slot={s}
                    onClick={() => {
                      setMenuOpen(false);
                      onAction(s.key);
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
